import {objectFromEntries} from '@benchling/js-utils';

import {assertAndContinue} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {DATA_TYPE_TO_USER_FACING_NAME} from 'data-frames/DataFrameConstants';
import {DataPointColumnsToDataType, DataType} from 'data-frames/DataFrameTypes';
import coerceOrFilterNulls from 'data-frames/util/legacy/coerceOrFilterNulls';
import inferAndValidateTypes, {TypesToCheck} from 'data-frames/util/legacy/inferAndValidateTypes';
import partitionDataset from 'data-frames/util/legacy/partitionDataset';
import {VISUALIZATION_TYPE_TO_READABLE} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import {makeColumnNameNotFoundErrorMessage} from 'insights/analytics-visualizations/viewers/prop-converters/cleanAndValidateDataFrame';
import ConvertToChartPropsException from 'insights/analytics-visualizations/viewers/prop-converters/ConvertToChartPropsException';
import {RunQueryOutputLegacyData} from 'insights/warehouse/AnalyticsWarehouseTypes';
import {capitalize, listToReadable} from 'util/NameUtil';
import {JSONValue} from 'util/Types';

// TODO(BNCH-24546) Unify dataset definitions with definitions in client/ui-library/charting/Types.ts
type OutputDatasets<OutputDataPoint, LabelColumn extends keyof OutputDataPoint | null> = Array<{
  points: Array<Omit<OutputDataPoint, LabelColumn extends keyof OutputDataPoint ? LabelColumn : never>>;
  label?: LabelColumn extends keyof OutputDataPoint ? OutputDataPoint[LabelColumn] : undefined;
}>;

/**
 * Defines how a column will be converted
 */
export interface BaseColumnInfo<OutputDataPoint> {
  // The name the column will have in the chart data output
  chartDataColumnName: keyof OutputDataPoint;
  // The name the column has in the RunQueryOutput object
  runQueryColumnName: string;
  // The allowable data types the column's values will be tested against
  possibleTypes: Array<DataType>;
  // Whether to remove null values
  filterNulls: boolean;
  // Whether this column is the series label. The data will be partitioned by the series label.
  // At most one column can be the series label.
  isSeriesLabel?: boolean;
}
export type NonSeriesLabelColumnInfo<OutputDataPoint> = BaseColumnInfo<OutputDataPoint> & {
  isSeriesLabel?: false;
};
export type SeriesLabelColumnInfo<
  OutputDataPoint,
  LabelColumn extends keyof OutputDataPoint | null,
> = BaseColumnInfo<OutputDataPoint> & {
  chartDataColumnName: LabelColumn;
  isSeriesLabel: true;
};
export type ColumnInfo<OutputDataPoint, LabelColumn extends keyof OutputDataPoint | null = null> =
  | NonSeriesLabelColumnInfo<OutputDataPoint>
  | SeriesLabelColumnInfo<OutputDataPoint, LabelColumn>;

/**
 * Given a RunQueryOutput return a dataset with the format of Array<DataPoint> where DataPoint is a row
 * represented by a map where keys are column names. Also return axisTypes, which is a map of column names
 * to DataType representing the type of the column.
 *
 * @template OutputDataPoint The type of the row in the converted dataset, should be a map where each key is
 *    the column name.
 * @template OutputColumnTypes The type of the axisTypes in the output. (See inferAndValidateTypes's docstring
 *    for details)
 * @param runQueryOutput RunQueryOutput data to convert
 * @param columnInfos List of objects that define how to convert each column. See ColumnInfo.
 * @param visualizationType VisualizationType (used to generate error messages)
 * @param extraVisualizationDetails Additional details for user-facing error message
 * @throws ConvertToChartPropsException if there are any errors in converting the data (such as incompatible
 *    data types). The exception's message is meant to be user facing.
 */
export default function runQueryOutputToChartData<
  OutputDataPoint extends {[key: string]: JSONValue},
  OutputColumnTypes extends DataPointColumnsToDataType<OutputDataPoint>,
  LabelColumn extends keyof OutputDataPoint | null = null,
>(
  runQueryOutput: RunQueryOutputLegacyData,
  columnInfos: Array<ColumnInfo<OutputDataPoint, LabelColumn>>,
  visualizationType: VisualizationType,
  extraVisualizationDetails?: string
): {
  datasets: OutputDatasets<OutputDataPoint, LabelColumn>;
  axisTypes: OutputColumnTypes;
} {
  assertAndContinue(
    columnInfos.filter((columnInfo) => columnInfo.isSeriesLabel).length < 2,
    'Expected at most one series label column.'
  );

  // @ts-expect-error Introduced by Typescript 5.1 upgrade
  // Error: Type 'keyof OutputDataPoint' is not assignable to type 'keyof OutputDataPoint & null'.
  const rowObjects = runQueryOutputToDataset(runQueryOutput, columnInfos);
  const columnTypeOutput = inferAndValidateTypes(
    rowObjects,
    objectFromEntries(
      columnInfos.map((columnInfo) => [columnInfo.chartDataColumnName as string, columnInfo.possibleTypes])
    ) as TypesToCheck<OutputDataPoint, OutputColumnTypes>
  );

  if (columnTypeOutput.valid === false) {
    const invalidColumn = columnTypeOutput.invalidColumns[0];
    const columnInfo = columnInfos.find((columnInfo) => columnInfo.chartDataColumnName === invalidColumn)!;
    throw new ConvertToChartPropsException(
      makeInvalidColumnTypeErrorMessage(
        invalidColumn,
        columnInfo.runQueryColumnName,
        columnInfo.possibleTypes,
        visualizationType,
        extraVisualizationDetails
      )
    );
  }

  const columnTypes = columnTypeOutput.columnTypes;
  const columnsToFilterNulls = columnInfos
    .filter((columnInfo) => columnInfo.filterNulls)
    .map((columnInfo) => columnInfo.chartDataColumnName);
  const labelColumn = columnInfos.find((columnInfo) => columnInfo.isSeriesLabel)?.chartDataColumnName ?? null;

  const cleanRowObjects = coerceOrFilterNulls(rowObjects, columnTypes, columnsToFilterNulls);
  const finalData = transformRowObjectsToDatasets(cleanRowObjects, labelColumn as LabelColumn);

  return {
    datasets: finalData,
    axisTypes: columnTypes,
  };
}

export function runQueryOutputToDataset<OutputDataPoint extends {[key: string]: JSONValue}>(
  runQueryOutput: RunQueryOutputLegacyData,
  columnInfos: Array<Pick<ColumnInfo<OutputDataPoint>, 'chartDataColumnName' | 'runQueryColumnName'>>
): Array<OutputDataPoint> {
  const columnIndices = columnInfos.map((columnInfo) => {
    const columnIndex = runQueryOutput.headers.indexOf(columnInfo.runQueryColumnName);
    if (columnIndex === -1) {
      throw new ConvertToChartPropsException(
        makeColumnNameNotFoundErrorMessage(
          columnInfo.chartDataColumnName as string,
          columnInfo.runQueryColumnName
        )
      );
    }
    return [columnInfo.chartDataColumnName, columnIndex] as [string, number];
  });

  return runQueryOutput.rows.map(
    (row) =>
      objectFromEntries(
        columnIndices.map(([columnName, columnIndex]) => [columnName, row[columnIndex]])
      ) as OutputDataPoint
  );
}

export function makeInvalidColumnTypeErrorMessage<DataPoint extends {[key: string]: unknown}>(
  outputColumnName: string & keyof DataPoint,
  runQueryColumnName: string,
  compatibleTypes: Array<DataType>,
  visualizationType: VisualizationType,
  extraVisualizationDetails?: string
): string {
  const errorTypes = listToReadable(
    compatibleTypes.map((dataType) => DATA_TYPE_TO_USER_FACING_NAME[dataType]),
    'or'
  );

  return `${capitalize(
    outputColumnName
  )} column "${runQueryColumnName}" contains an invalid data type. ${pluralize(
    2,
    VISUALIZATION_TYPE_TO_READABLE[visualizationType]
  )} ${
    extraVisualizationDetails ? `${extraVisualizationDetails} ` : ''
  }only support ${errorTypes} ${outputColumnName} columns.`;
}

/**
 * @deprecated
 */
export function transformRowObjectsToDatasets<
  OutputDataPoint,
  LabelColumn extends keyof OutputDataPoint | null,
>(
  dataset: Array<OutputDataPoint>,
  labelColumn: LabelColumn | null
): OutputDatasets<OutputDataPoint, LabelColumn> {
  if (dataset.length === 0) {
    return [];
  }
  if (labelColumn == null) {
    return [
      {
        points: dataset,
      },
    ];
  } else {
    return partitionDataset(dataset, labelColumn as keyof OutputDataPoint).map(({label, points}) => ({
      label,
      points,
    })) as OutputDatasets<OutputDataPoint, LabelColumn>;
  }
}
